import React, { useState, useEffect } from 'react';
import { Trophy, Target, Calendar, BarChart3, TrendingUp, Users, Award, Star, X, ChevronLeft, ChevronRight, List, Clock, AlertCircle, Home } from 'lucide-react';
import { api } from '../api';
import './ProgressView.css';

// Simple CSS Bar Chart Component
const BarChart = ({ data, labelKey, valueKey, color = '#e8c447', maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0), 1);
  
  return (
    <div className="bar-chart">
      {data.map((item, index) => {
        const value = item[valueKey] || 0;
        const height = max > 0 ? (value / max) * 100 : 0;
        const label = item[labelKey];
        
        return (
          <div key={index} className="bar-item">
            <div className="bar-wrapper">
              <div 
                className="bar" 
                style={{ height: `${height}%`, backgroundColor: color }}
                title={`${label}: ${value}`}
              />
            </div>
            <span className="bar-label">{label}</span>
            <span className="bar-value">{value}</span>
          </div>
        );
      })}
    </div>
  );
};

// Horizontal Bar Chart for categories
const HorizontalBarChart = ({ data, onCategoryClick }) => {
  const max = Math.max(...data.map(d => d.points || 0), 1);
  
  return (
    <div className="horizontal-bar-chart">
      {data.map((item, index) => {
        const percentage = (item.points / max) * 100;
        const colors = {
          home: '#3b82f6',
          errands: '#8b5cf6',
          finance: '#10b981',
          health: '#ef4444',
          work: '#f59e0b',
          personal: '#6b7280',
          educational: '#ec4899'
        };
        
        return (
          <div 
            key={index} 
            className={`h-bar-item ${onCategoryClick ? 'clickable' : ''}`}
            onClick={() => onCategoryClick && onCategoryClick(item.category)}
            title={onCategoryClick ? `View ${item.category} chores` : ''}
          >
            <span className="h-bar-label">{item.category}</span>
            <div className="h-bar-wrapper">
              <div 
                className="h-bar" 
                style={{ width: `${percentage}%`, backgroundColor: colors[item.category] || '#e8c447' }}
              />
            </div>
            <span className="h-bar-value">{item.points} pts</span>
          </div>
        );
      })}
    </div>
  );
};

// Leaderboard Entry Component
const LeaderboardEntry = ({ entry, index }) => {
  const isTopThree = index < 3;
  const medals = ['🥇', '🥈', '🥉'];
  
  return (
    <div className={`leaderboard-entry ${entry.isCurrentUser ? 'current-user' : ''}`}>
      <div className="rank">
        {isTopThree ? (
          <span className="medal">{medals[index]}</span>
        ) : (
          <span className="rank-number">{entry.rank}</span>
        )}
      </div>
      <div className="user-info">
        <span className="user-name">{entry.name}</span>
        <span className={`user-role role-${entry.role}`}>{entry.role}</span>
      </div>
      <div className="stats">
        <div className="stat">
          <Star size={14} className="stat-icon" />
          <span>{entry.total_points} pts</span>
        </div>
        <div className="stat">
          <Target size={14} className="stat-icon" />
          <span>{entry.completed_tasks} tasks</span>
        </div>
      </div>
    </div>
  );
};

// Main Progress View Component
export default function ProgressView({ currentUser, onClose, onFilterChores }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [adminReport, setAdminReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Admin report date filters
  const [reportFilter, setReportFilter] = useState('all-time');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (filter = null, start = null, end = null) => {
    setLoading(true);
    try {
      const [progressData, leaderboardData] = await Promise.all([
        api.getProgress(),
        api.getLeaderboard()
      ]);
      
      setProgress(progressData);
      setLeaderboard(leaderboardData.leaderboard || []);
      
      // Load admin report if user is admin
      if (currentUser?.role === 'admin') {
        const useFilter = filter || reportFilter;
        const useStart = start || (useFilter === 'custom' ? customStartDate : null);
        const useEnd = end || (useFilter === 'custom' ? customEndDate : null);
        const report = await api.getAdminProgressReport(useFilter, useStart, useEnd);
        setAdminReport(report);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to load progress data:', err);
      setError('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (newFilter) => {
    setReportFilter(newFilter);
    loadData(newFilter);
  };

  if (loading) {
    return (
      <div className="progress-view-overlay">
        <div className="progress-view loading">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="progress-view-overlay">
        <div className="progress-view error">
          <p>{error}</p>
          <button onClick={() => loadData()}>Retry</button>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  // Format weekly data for chart
  const weeklyChartData = progress?.weeklyData?.map(d => ({
    label: new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' }),
    value: d.points
  })) || [];

  // Fill in missing days for weekly chart
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (6 - i));
    return {
      label: days[d.getDay()],
      value: progress?.weeklyData?.find(w => 
        new Date(w.date).toDateString() === d.toDateString()
      )?.points || 0
    };
  });

  return (
    <div className="progress-view-overlay">
      <div className="progress-view">
        {/* Header */}
        <div className="progress-header">
          <h2 className="progress-title">
            <BarChart3 size={24} />
            Activity Progress
          </h2>
          <button className="home-btn" onClick={onClose}>
            <Home size={18} />
            <span>Dashboard</span>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="progress-tabs">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            <TrendingUp size={16} />
            Overview
          </button>
          <button 
            className={activeTab === 'leaderboard' ? 'active' : ''}
            onClick={() => setActiveTab('leaderboard')}
          >
            <Trophy size={16} />
            Leaderboard
          </button>
          {currentUser?.role === 'admin' && (
            <button 
              className={activeTab === 'report' ? 'active' : ''}
              onClick={() => setActiveTab('report')}
            >
              <Users size={16} />
              Team Report
            </button>
          )}
        </div>

        {/* Content */}
        <div className="progress-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-tab">
              {/* Stats Cards */}
              <div className="stats-row">
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ status: 'all' })}
                  title={onFilterChores ? 'View all chores' : ''}
                >
                  <List size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.totalChores || 0}</span>
                    <span className="stat-label">Total</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ status: 'pending' })}
                  title={onFilterChores ? 'View pending chores' : ''}
                >
                  <Clock size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.pendingCount || 0}</span>
                    <span className="stat-label">Pending</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ status: 'completed' })}
                  title={onFilterChores ? 'View completed chores' : ''}
                >
                  <Target size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.completedCount || 0}</span>
                    <span className="stat-label">Complete</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ priority: 'high' })}
                  title={onFilterChores ? 'View high priority chores' : ''}
                >
                  <AlertCircle size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.highPriorityCount || 0}</span>
                    <span className="stat-label">High Priority</span>
                  </div>
                </div>
                <div 
                  className={`stat-card-large ${onFilterChores ? 'clickable' : ''}`}
                  onClick={() => onFilterChores && onFilterChores({ dueToday: true })}
                  title={onFilterChores ? 'View chores due today' : ''}
                >
                  <Calendar size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{progress?.dueTodayCount || 0}</span>
                    <span className="stat-label">Due Today</span>
                  </div>
                </div>
              </div>

              {/* Weekly Chart */}
              <div className="chart-section">
                <h3>
                  <Calendar size={18} />
                  Last 7 Days
                </h3>
                <BarChart 
                  data={last7Days} 
                  labelKey="label" 
                  valueKey="value"
                  color="#e8c447"
                />
              </div>

              {/* Category Breakdown */}
              {progress?.categoryData?.length > 0 && (
                <div className="chart-section">
                  <h3>
                    <BarChart3 size={18} />
                    Points by Category
                  </h3>
                  <HorizontalBarChart 
                    data={progress.categoryData} 
                    onCategoryClick={(category) => onFilterChores && onFilterChores({ category })}
                  />
                </div>
              )}
            </div>
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <div className="leaderboard-tab">
              <div className="leaderboard-header">
                <h3>Top Performers</h3>
                <p>Based on total points earned</p>
              </div>
              <div className="leaderboard-list">
                {leaderboard.map((entry, index) => (
                  <LeaderboardEntry key={entry.id} entry={entry} index={index} />
                ))}
              </div>
            </div>
          )}

          {/* Admin Report Tab */}
          {activeTab === 'report' && currentUser?.role === 'admin' && adminReport && (
            <div className="report-tab">
              {/* Date Filter */}
              <div className="report-filters">
                <label>Period:</label>
                <select 
                  value={reportFilter} 
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className="filter-select"
                >
                  <option value="all-time">All Time</option>
                  <option value="current-month">Current Month</option>
                  <option value="current-week">Current Week</option>
                  <option value="last-week">Last Week</option>
                  <option value="custom">Custom Range</option>
                </select>
                
                {reportFilter === 'custom' && (
                  <div className="custom-date-range">
                    <input 
                      type="date" 
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      placeholder="Start Date"
                    />
                    <span>to</span>
                    <input 
                      type="date" 
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      placeholder="End Date"
                    />
                    <button 
                      onClick={() => loadData('custom', customStartDate, customEndDate)}
                      disabled={!customStartDate || !customEndDate}
                      className="apply-btn"
                    >
                      Apply
                    </button>
                  </div>
                )}
                
                {adminReport.filter && adminReport.filter !== 'all-time' && (
                  <span className="filter-badge">{adminReport.filter.replace('-', ' ')}</span>
                )}
              </div>

              {/* Group Summary */}
              <div className="stats-row">
                <div className="stat-card-large">
                  <Users size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{adminReport.groupTotals?.total_group_points || 0}</span>
                    <span className="stat-label">Team Total Points</span>
                  </div>
                </div>
                <div className="stat-card-large">
                  <Target size={32} className="stat-icon" />
                  <div className="stat-info">
                    <span className="stat-value">{adminReport.groupTotals?.total_completions || 0}</span>
                    <span className="stat-label">Team Completions</span>
                  </div>
                </div>
              </div>

              {/* Daily Chart */}
              {adminReport.dailyChart?.length > 0 && (
                <div className="chart-section">
                  <h3>
                    <Calendar size={18} />
                    {adminReport.filter === 'all-time' ? 'Last 30 Days' : adminReport.filter.replace('-', ' ')} Activity
                  </h3>
                  <BarChart 
                    data={adminReport.dailyChart.map(d => ({
                      label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      value: d.points
                    }))} 
                    labelKey="label" 
                    valueKey="value"
                    color="#3b82f6"
                  />
                </div>
              )}

              {/* User Details Table */}
              <div className="chart-section">
                <h3>
                  <Users size={18} />
                  Team Member Stats
                </h3>
                <div className="user-stats-table">
                  <div className="table-header">
                    <span>Rank</span>
                    <span>Name</span>
                    <span>Points</span>
                    <span>Tasks</span>
                  </div>
                  {adminReport.users?.map((user, index) => (
                    <div key={user.id} className={`table-row ${user.id === currentUser.id ? 'current-user' : ''}`}>
                      <span className="rank">{index + 1}</span>
                      <span className="name">{user.name}</span>
                      <span className="points">{user.total_points}</span>
                      <span className="tasks">{user.completed_tasks}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
